import { useEffect, useRef } from "react";
import { claimSubmitApplication } from "../lib/api";
import { reportHandledError } from "../lib/bugsnag";
import { pushSubmitApplication } from "../lib/gtm-events";

const MAX_ATTEMPTS = 3;
const RETRY_BASE_MS = 2_000;

function isRetryable(err: unknown): boolean {
  const status = (err as { status?: unknown } | null)?.status;
  return typeof status !== "number" || status >= 500;
}

/** MKT-622: claims the once-per-user SubmitApplication conversion while
 *  `pending` is true. Network and 5xx failures are tried up to three times
 *  with backoff while the page stays mounted. The server's compare-and-set
 *  decides who fires; this hook only pushes what the server hands back. */
export function useSubmitApplicationClaim(pending: boolean): void {
  const started = useRef(false);

  useEffect(() => {
    if (!pending || started.current) return;
    started.current = true;
    let timer: number | undefined;
    let active = true;

    const attempt = (n: number): void => {
      claimSubmitApplication().then(
        (claim) => {
          // A retry that finds the claim taken means an earlier attempt in
          // this page won on the server but its response never arrived, so
          // push the stored event_id (Meta dedupes on it). A first attempt
          // that finds it taken is a returning user: never push.
          const recovered = !claim.fire && n > 0;
          if (!claim.fire && !recovered) return;
          if (typeof claim.eventId !== "string" || claim.eventId === "") {
            reportHandledError(new Error("submit-application claim returned no event_id"), {
              surface: "submit_application_claim",
              recovered,
            });
            return;
          }
          pushSubmitApplication(claim.eventId, claim.propertyCount);
        },
        (err: unknown) => {
          if (active && isRetryable(err) && n + 1 < MAX_ATTEMPTS) {
            timer = window.setTimeout(
              () => {
                timer = undefined;
                attempt(n + 1);
              },
              RETRY_BASE_MS * 3 ** n,
            );
            return;
          }
          if (!active) {
            // Unmounted mid-retry: let the next mount try again.
            started.current = false;
            return;
          }
          console.warn("SubmitApplication claim failed:", err);
          reportHandledError(err, { surface: "submit_application_claim", attempts: n + 1 });
        },
      );
    };
    attempt(0);

    return () => {
      active = false;
      if (timer !== undefined) {
        window.clearTimeout(timer);
        started.current = false;
      }
    };
  }, [pending]);
}
